import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { MetaradarQueryKeys } from '@app/model/metaradar/service/MetaradarQueryKeys';
import MetaradarService from '@app/model/metaradar/service/MetaradarService';
import { MetaradarStateSnapshot } from '@app/model/metaradar/MetaradarStateSnapshot';
import { MetaradarRefreshState, MetaradarRefreshStatus } from '@app/model/metaradar/MetaradarRefreshStatus';


export function useGetMetaradarStateSnapshotQuery(companyHotelId: number, options?: Omit<UseQueryOptions<CompanyHotel, unknown, MetaradarStateSnapshot | undefined>, 'queryKey' | 'queryFn'>) {
	return useQuery({
		queryKey: MetaradarQueryKeys.companyHotel(companyHotelId),
		queryFn: () => MetaradarService.getMetaradarStateSnapshot(companyHotelId),
		refetchOnMount: 'always',
		...options,
		// The select option ensures the data is always a class instance, even after cache rehydration.
		select: (data: MetaradarStateSnapshot | Record<string, any>) => {
			if (!data) {
				return undefined;
			}
			return data instanceof MetaradarStateSnapshot ? data : new MetaradarStateSnapshot(data as MetaradarStateSnapshot);
		},
	});
}

export function useGetMetaradarRefreshStatusQuery(companyHotelId: number) {
	return useQuery({
		queryKey: MetaradarQueryKeys.refreshStatus(companyHotelId),
		queryFn: () => MetaradarService.getMetaradarRefreshStatus(companyHotelId),
		select: data => new MetaradarRefreshStatus(data),
		refetchOnMount: 'always',
		refetchOnWindowFocus: true,
		refetchInterval: query => {
			if (query.state.data?.status === MetaradarRefreshState.IN_PROGRESS) return 2_000;
			if (query.state.data?.status === MetaradarRefreshState.COOLDOWN) {
				return Math.min(30_000, Math.max(1, query.state.data.retryAfterSeconds ?? 1) * 1_000);
			}
			return false;
		},
	});
}

export function useTriggerMetaradarRefreshMutation(companyHotelId: number) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: MetaradarQueryKeys.refreshStatus(companyHotelId),
		mutationFn: MetaradarService.triggerMetaradarRefresh,
		onSuccess: (data, { companyHotelId }) => {
			queryClient.setQueryData(MetaradarQueryKeys.refreshStatus(companyHotelId), new MetaradarRefreshStatus(data));
			queryClient.invalidateQueries({ queryKey: MetaradarQueryKeys.companyHotel(companyHotelId) });
		},
	});
}
